import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Order, OrderReadiness } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './flow.css';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

type Phase = { key: string; label: string; done: boolean; current: boolean; detail?: string };

/** Die Phasen eines Auftrags aus seinen Daten: Anfrage, Angebot, Auftrag, Übergabe, Planung, Umsetzung, Abrechnung */
function phases(o: Order): Phase[] {
  const handed = !!o.handedOverAt;
  const planned = ['planned', 'in_progress', 'done', 'invoiced'].includes(o.status);
  const started = ['in_progress', 'done', 'invoiced'].includes(o.status);
  const finished = o.status === 'done' || o.status === 'invoiced';
  const current = o.status === 'cancelled' ? '' : o.status === 'invoiced' ? 'invoice' : o.status === 'done' ? 'invoice' : o.status === 'in_progress' ? 'work' : o.status === 'planned' ? 'work' : handed ? 'planning' : 'order';
  return [
    { key: 'inquiry', label: 'Anfrage', done: !!o.inquiryId, current: false, detail: o.inquiryReference ?? 'ohne Anfrage' },
    { key: 'offer', label: 'Angebot', done: !!o.offerId, current: false, detail: o.offerNumber ?? 'Direktauftrag' },
    { key: 'order', label: 'Auftrag', done: true, current: current === 'order', detail: o.number },
    { key: 'handover', label: 'Übergabe', done: handed || planned, current: current === 'planning' && !planned, detail: o.handedOverAt ? stamp.format(new Date(o.handedOverAt)).slice(0, 10) : undefined },
    { key: 'planning', label: 'Planung', done: planned, current: current === 'planning', detail: o.plannedAt ? stamp.format(new Date(o.plannedAt)).slice(0, 10) : undefined },
    { key: 'work', label: 'Umsetzung', done: finished, current: current === 'work', detail: started ? (finished ? 'erledigt' : 'läuft') : undefined },
    { key: 'invoice', label: 'Abrechnung', done: o.status === 'invoiced', current: current === 'invoice' && o.status === 'done', detail: o.status === 'invoiced' ? 'abgerechnet' : undefined },
  ];
}

function Check({ ok, children, hint }: { ok: boolean; children: string; hint?: string }) {
  return (
    <li className={ok ? 'is-ok' : 'is-open'}>
      <span aria-hidden="true">{ok ? '✓' : '○'}</span>
      <span>{children}{!ok && hint && <span className="muted small"> – {hint}</span>}</span>
    </li>
  );
}

/**
 * Auftragsablauf (KON-172): zeigt, in welcher Phase der Auftrag steht, und bietet den nächsten Schritt an. Die Auftragsabwicklung bereitet vor und übergibt an das Personalmanagement,
 * das Personalmanagement plant (Aufgaben, Mitarbeiter, Fahrzeuge unten auf der Seite), schließt die Planung ab oder gibt den Auftrag mit einer Frage zurück. Danach folgen Umsetzung
 * und Abrechnung mit den bekannten Schaltern.
 */
export function OrderFlow({ order: o }: { order: Order }) {
  const client = useQueryClient();
  const { can, hasModule } = useSession();
  const write = can('orders.write');
  const plan = can('orders.plan');
  const [note, setNote] = useState('');
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const open = o.status === 'open';
  const handed = !!o.handedOverAt;
  const readiness = useQuery({
    queryKey: ['order-readiness', o.id, o.updatedAt],
    queryFn: async () => (await api<{ data: OrderReadiness }>('GET', `/orders/${o.id}/readiness`)).data,
    enabled: (open || o.status === 'planned') && (handed || write || plan),
  });
  const r = readiness.data;

  const done = (next: Order) => {
    client.setQueryData(['order', next.id], next);
    for (const key of ['orders', 'order-pipeline', 'order-planning', 'order-readiness']) client.invalidateQueries({ queryKey: [key] });
    setError(null);
    setReturning(false);
    setReason('');
    setNote('');
  };
  const act = useMutation({
    mutationFn: async (run: () => Promise<{ data: Order }>) => (await run()).data,
    onSuccess: done,
    onError: (e) => setError(errorText(e, 'Das hat nicht geklappt.')),
  });

  if (o.status === 'cancelled') return null;
  const steps = phases(o);
  const readyToHandOver = !!o.serviceStart;

  return (
    <section className="card flow" aria-labelledby="flow-title">
      <h2 id="flow-title">Auftragsablauf</h2>
      <ol className="flow-steps" aria-label="Phasen">
        {steps.map((s) => (
          <li key={s.key} className={`flow-step${s.done ? ' is-done' : ''}${s.current ? ' is-current' : ''}`} aria-current={s.current ? 'step' : undefined}>
            <span className="flow-step__dot" aria-hidden="true">{s.done ? '✓' : ''}</span>
            <span className="flow-step__label">{s.label}</span>
            {s.detail && <span className="flow-step__detail">{s.key === 'inquiry' && o.inquiryId ? <Link to={`/anfragen/${o.inquiryId}`}>{s.detail}</Link> : s.key === 'offer' && o.offerId ? <Link to={`/angebote/${o.offerId}`}>{s.detail}</Link> : s.detail}</span>}
          </li>
        ))}
      </ol>
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {/* Vorbereitung durch die Auftragsabwicklung */}
      {open && !handed && (
        <div className="flow-next">
          <h3>Nächster Schritt: an das Personalmanagement übergeben</h3>
          <p className="muted small">Die Auftragsabwicklung prüft den Auftrag und übergibt ihn mit einem Hinweis. Das Personalmanagement plant danach die Umsetzung.</p>
          <ul className="flow-checks">
            <Check ok hint="">Auftrag mit Positionen angelegt</Check>
            <Check ok={readyToHandOver} hint="Der Leistungszeitraum fehlt, die Planung braucht ihn">Leistungszeitraum eingetragen</Check>
            <Check ok={!!o.customer.email || !!o.customer.street} hint="Kunde ohne E-Mail oder Anschrift, die Planung kann ihn nicht erreichen">Kunde erreichbar</Check>
          </ul>
          {write ? (
            <>
              <label className="field">
                <span>Hinweis für die Planung (freiwillig)</span>
                <textarea className="input textarea textarea--short" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Zum Beispiel: Kunde möchte Samstag, Zugang über die Nachbarin, 3. Etage ohne Aufzug" />
              </label>
              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={act.isPending}
                  onClick={() => (readyToHandOver || window.confirm('Der Leistungszeitraum fehlt. Trotzdem übergeben?')) && act.mutate(() => api<{ data: Order }>('POST', `/orders/${o.id}/handover`, { note: note.trim() || undefined }))}
                >
                  {act.isPending ? 'Wird übergeben …' : 'An Personalmanagement übergeben'}
                </button>
              </div>
            </>
          ) : (
            <p className="muted small">Zum Übergeben fehlt Ihrer Rolle das Recht, Aufträge zu bearbeiten.</p>
          )}
        </div>
      )}

      {/* Planung durch das Personalmanagement */}
      {(open && handed) && (
        <div className="flow-next">
          <h3>Nächster Schritt: Planung durch das Personalmanagement</h3>
          <p className="small">Übergeben am {stamp.format(new Date(o.handedOverAt!))} Uhr{o.handedOverByName ? ` von ${o.handedOverByName}` : ''}.</p>
          {o.handoverNote && <blockquote className="flow-note">{o.handoverNote}</blockquote>}
          {hasModule('calendar') || hasModule('fleet') ? <ReadinessList r={r} hasFleet={hasModule('fleet')} /> : null}
          <p className="muted small">Aufgaben, Mitarbeiter und Fahrzeuge planen Sie weiter unten auf dieser Seite, die Auftragsplanung zeigt alle übergebenen Aufträge.</p>
          <div className="form-actions">
            {plan && (
              <button
                type="button"
                className="btn btn--primary"
                disabled={act.isPending}
                onClick={() => (r?.ready || window.confirm('Die Planung ist noch nicht vollständig (Leistungszeitraum, Aufgaben mit Zuständigen). Trotzdem abschließen?')) && act.mutate(() => api<{ data: Order }>('POST', `/orders/${o.id}/planning/complete`))}
              >
                Planung abschließen
              </button>
            )}
            {plan && <button type="button" className="btn btn--secondary" onClick={() => setReturning((v) => !v)}>Mit Frage zurückgeben …</button>}
            {write && <button type="button" className="btn btn--secondary" disabled={act.isPending} onClick={() => window.confirm('Die Übergabe zurücknehmen?') && act.mutate(() => api<{ data: Order }>('DELETE', `/orders/${o.id}/handover`))}>Übergabe zurücknehmen</button>}
          </div>
          {returning && <ReturnForm reason={reason} setReason={setReason} pending={act.isPending} onSend={() => act.mutate(() => api<{ data: Order }>('POST', `/orders/${o.id}/handover/return`, { reason: reason.trim() }))} onCancel={() => setReturning(false)} />}
        </div>
      )}

      {o.status === 'planned' && (
        <div className="flow-next">
          <h3>Geplant, bereit zur Umsetzung</h3>
          <p className="small">Die Planung ist abgeschlossen{o.plannedAt ? ` (${stamp.format(new Date(o.plannedAt))} Uhr)` : ''}. {write ? 'Mit „In Arbeit“ oben beginnt die Umsetzung.' : ''}</p>
          <ReadinessList r={r} hasFleet={hasModule('fleet')} />
          {plan && handed && (
            <>
              <div className="form-actions"><button type="button" className="btn btn--secondary" onClick={() => setReturning((v) => !v)}>Mit Frage zurückgeben …</button></div>
              {returning && <ReturnForm reason={reason} setReason={setReason} pending={act.isPending} onSend={() => act.mutate(() => api<{ data: Order }>('POST', `/orders/${o.id}/handover/return`, { reason: reason.trim() }))} onCancel={() => setReturning(false)} />}
            </>
          )}
        </div>
      )}
      {o.status === 'in_progress' && <p className="muted small flow-line">Der Auftrag wird umgesetzt. Mit „Erledigt“ oben schließen Sie die Umsetzung ab.</p>}
      {o.status === 'done' && <p className="muted small flow-line">Die Umsetzung ist abgeschlossen. Als Nächstes folgt die Rechnung (Bereich „Rechnungen“ weiter unten).</p>}
    </section>
  );
}

function ReadinessList({ r, hasFleet }: { r: OrderReadiness | undefined; hasFleet: boolean }) {
  if (!r) return <p className="muted small">Wird geprüft …</p>;
  return (
    <ul className="flow-checks" aria-label="Stand der Planung">
      <Check ok={r.hasServiceDates} hint="Leistungszeitraum fehlt">Leistungszeitraum eingetragen</Check>
      <Check ok={r.tasks > 0} hint="noch keine Aufgabe zum Auftrag">{`${r.tasks} ${r.tasks === 1 ? 'Aufgabe' : 'Aufgaben'} verknüpft`}</Check>
      <Check ok={r.tasks > 0 && r.tasksAssigned === r.tasks} hint={r.tasks > 0 ? `${r.tasks - r.tasksAssigned} ohne zuständigen Mitarbeiter` : 'Aufgaben brauchen Zuständige'}>{`${r.tasksAssigned} von ${r.tasks} Aufgaben mit Zuständigem`}</Check>
      <li className={r.shifts > 0 ? 'is-ok' : 'is-info'}><span aria-hidden="true">{r.shifts > 0 ? '✓' : '·'}</span><span>{r.shifts > 0 ? `${r.shifts} Aufgaben im Schichtplan` : 'Noch nicht im Schichtplan (freiwillig)'}</span></li>
      {hasFleet && <li className={(r.vehicles ?? 0) > 0 ? 'is-ok' : 'is-info'}><span aria-hidden="true">{(r.vehicles ?? 0) > 0 ? '✓' : '·'}</span><span>{(r.vehicles ?? 0) > 0 ? `${r.vehicles} Fahrzeug${r.vehicles === 1 ? '' : 'e'} eingeplant` : 'Kein Fahrzeug eingeplant (freiwillig)'}</span></li>}
    </ul>
  );
}

function ReturnForm({ reason, setReason, pending, onSend, onCancel }: { reason: string; setReason: (v: string) => void; pending: boolean; onSend: () => void; onCancel: () => void }) {
  return (
    <form className="flow-return" onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 2) onSend(); }}>
      <label className="field">
        <span>Frage oder Grund für die Auftragsabwicklung</span>
        <textarea className="input textarea textarea--short" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} required placeholder="Zum Beispiel: Welche Etage ist es? Gibt es einen Aufzug?" />
      </label>
      <div className="form-actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={pending || reason.trim().length < 2}>Zurückgeben</button>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
